








































































import { Space } from '@/types/GlobalTypes';
import CoCard from '@/components/Molecules/co-card/CoCard.vue';
import CoHeadline from '@/components/Atoms/co-headline/CoHeadline.vue';
import CoPrice from '@/components/Atoms/co-price/coPrice.vue';
import CoPill from '@/components/Atoms/co-pill/CoPill.vue';
import CoRoundButton from '@/components/Atoms/co-round-button/CoRoundButton.vue';
import CoInput from '@/components/Molecules/co-input/CoInput.vue';
import CoButton from '@/components/Atoms/co-button/CoButton.vue';

interface EventTicket {
    ID?: string;
    Name: string;
    Description?: string;
    PriceInCents: number;
    TaxRateID?: string;
    Limit?: number;
    SoldCount?: number;
    TicketsLeft?: number;
    SoldOut?: boolean;
    CreatedAt?: number;
    UpdatedAt?: number;
    CancellationNotice?: number;
    CancellationFee?: number;
    TermsAndConditions?: string;
    Delete?: boolean;
}

interface Price {
    PriceInCents: number;
    Currency: string;
    TaxRate: number;
    TaxName: string;
    Inclusive: boolean;
    Unit?: string;
}

export default {
    name: 'CoCardTicket',
    components: {
        CoPrice,
        CoCard,
        CoHeadline,
        CoPill,
        CoRoundButton,
        CoInput,
    },
    props: {
        ticket: {
            type: Object as () => EventTicket,
            required: true,
        },
        taxRates: {
            type: Array as () => Array<any> | null,
            default: () => [],
        },
        currency: {
            type: [String, null],
            default: null,
        },
        selected: {
            type: Boolean,
            default: false,
            required: false,
        },
        quantity: {
            type: Number,
            default: 0,
        },
        totalSelectedQuantity: {
            type: Number,
            default: 0,
        },
        maxTotalQuantity: {
            type: Number,
            default: 0,
        },
        originCommunity: {
            type: Object as () => Space | null,
            default: null,
        },
        currentCommunityID: {
            type: String,
            default: '',
        },
        eventSlug: {
            type: String,
            default: '',
        },
    },
    computed: {
        redirectToOriginCommunity() {
            // if ticket price is 0, we don't redirect to origin community, as user can get the ticket for free in current community
            if (this.ticket.PriceInCents === 0) {
                return false;
            }
            // if there is no origin community or its Domain is missing, do not redirect
            if (!this.originCommunity || !this.originCommunity.Domain) {
                return false;
            }
            if (this.originCommunity.ID === this.currentCommunityID) {
                return false;
            }
            return true;
        },
        redirectUrl() {
            if (!this.redirectToOriginCommunity || !this.originCommunity || !this.originCommunity.Domain) {
                return null;
            }
            return `https://${this.originCommunity.Domain}/events/${this.eventSlug}`;
        },

        iconName() {
            return this.selected ? 'circle-fill' : 'circle';
        },
        classes() {
            return {
                default: !this.ticket.SoldOut,
                'sold-out': this.ticket.SoldOut,
            };
        },
        maxQuantity() {
            // Public checkout exposes TicketsLeft; older member responses expose Limit and SoldCount.
            // Keep zero stock distinct from an unlimited ticket (null).
            if (this.ticket.SoldOut) {
                return 0;
            }
            if (typeof this.ticket.TicketsLeft === 'number') {
                return Math.max(this.ticket.TicketsLeft, 0);
            }
            if (this.ticket.Limit > 0) {
                return Math.max(this.ticket.Limit - (this.ticket.SoldCount || 0), 0);
            }
            return null;
        },
        minQuantity() {
            return 0;
        },
        totalLimitRemaining() {
            if (!this.maxTotalQuantity || this.maxTotalQuantity <= 0) {
                return null;
            }
            return Math.max(this.maxTotalQuantity - (this.totalSelectedQuantity - this.quantity), 0);
        },
        effectiveMaxQuantity() {
            const perTicketMax = this.maxQuantity;
            const totalMax = this.totalLimitRemaining;
            if (perTicketMax === null && totalMax === null) {
                return null;
            }
            if (perTicketMax === null) {
                return totalMax;
            }
            if (totalMax === null) {
                return perTicketMax;
            }
            return Math.min(perTicketMax, totalMax);
        },
        inputMaxQuantity() {
            if (this.effectiveMaxQuantity === null) {
                return 999999;
            }
            return this.effectiveMaxQuantity;
        },
        canIncrement() {
            if (this.effectiveMaxQuantity === null) {
                return true;
            }
            return this.ticketQuantity < this.effectiveMaxQuantity;
        },
    },
    watch: {
        quantity: {
            handler(next) {
                const normalized = Number(next) || 0;
                if (normalized !== this.ticketQuantity) {
                    this.ticketQuantity = normalized;
                }
            },
            immediate: true,
        },
    },
    data() {
        return {
            ticketQuantity: this.quantity || 0,
        };
    },
    methods: {
        goToOriginCommunity() {
            if (this.redirectUrl) {
                window.open(this.redirectUrl, '_blank');
            }
        },
        add() {
            if (!this.canIncrement) {
                this.$emit('max-total-reached');
                return;
            }
            this.ticketQuantity += 1;
            this.updateTicketQuantity();
        },
        remove() {
            if (this.ticketQuantity > this.minQuantity) {
                this.ticketQuantity -= 1;
                this.updateTicketQuantity();
            }
        },
        updateTicketQuantity(value = this.ticketQuantity) {
            // Use the change payload: CoInput emits it before its v-model watcher runs.
            const quantity = Math.max(Math.trunc(Number(value)) || 0, 0);
            this.ticketQuantity = Math.min(quantity, this.inputMaxQuantity);
            this.$emit('update-ticket-quantity', {
                id: this.ticket.ID,
                ticket: this.ticket,
                quantity: this.ticketQuantity,
            });
        },
        priceObject() {
            const priceObj: Price = {
                PriceInCents: this.ticket.PriceInCents,
                Currency: this.currency || '',
                TaxName: '',
                TaxRate: 0,
                Inclusive: false,
            };

            const taxRate = this.taxRates.find((tr) => tr.ID === this.ticket.TaxRateID);
            if (taxRate) {
                priceObj.TaxRate = taxRate.Percentage;
                priceObj.TaxName = taxRate.Name;
                priceObj.Inclusive = taxRate.Inclusive;
            }

            return priceObj;
        },

        click(event) {
            if (this.ticket.SoldOut) {
                return;
            }
            const r = JSON.parse(JSON.stringify(this.ticket));
            this.$emit('click', r);
        },
    },
};
